import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";

import { organicJobDeliveryQueryKeys } from "../api/query-keys";

import { URL_PARAMS } from "@/constants/urlParams";
import type { TableDataHook } from "@/features/integrations/shared/renderer/hooks/types";
import { instance } from "@/lib/axios/instance";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";

type OrganicVacanciesResponse = {
  data: unknown[];
  page: number;
  pageSize: number;
  totalRecords: number;
};

/**
 * Creates a table data hook for the organic module client detail page.
 * Integration name is captured via closure so the hook stays generic.
 */
export function createOrganicVacanciesTableDataHook(integrationName: string): TableDataHook {
  return function useOrganicVacanciesTableData(params: {
    clientId: string | undefined;
    tenantId: string | undefined;
    page: number;
    pageSize: number;
  }): UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError> {
    const { clientId, tenantId, page, pageSize } = params;
    // The page component owns the search-input debounce + URL writes — reading the URL
    // here is already debounced. Sort state goes through `useSortSearchParams` so the
    // parser stays consistent with the rest of the app.
    const [searchParams] = useSearchParams();
    const debouncedSearchValue = searchParams.get(URL_PARAMS.SEARCH) || undefined;
    const { orderBy: rawOrderBy, descending: rawDescending } = useSortSearchParams();
    const orderBy = rawOrderBy ?? undefined;
    const descending = rawDescending ?? undefined;

    return useQuery<OrganicVacanciesResponse, AxiosError>({
      queryKey: organicJobDeliveryQueryKeys.vacanciesList({
        integrationName,
        clientId: clientId ?? "",
        tenantId: tenantId ?? "",
        page,
        pageSize,
        search: debouncedSearchValue,
        orderBy,
        descending,
      }),
      queryFn: () =>
        instance
          .get<OrganicVacanciesResponse>(
            `/api/v2/Integrations/${encodeURIComponent(integrationName)}/organic/vacancies`,
            {
              params: {
                clientId,
                tenantId,
                page,
                pageSize,
                ...(debouncedSearchValue && { search: debouncedSearchValue }),
                ...(orderBy && { orderBy }),
                ...(descending !== undefined &&
                  descending !== null && {
                  descending: descending.toString(),
                }),
              },
            }
          )
          .then((res) => res.data),
      enabled: !!clientId && !!tenantId,
      refetchOnReconnect: false,
      staleTime: 20 * 60 * 1000,
    });
  };
}
